export const MAX_HERO_LEVEL = 100;

function normalizeLevel(level: number): number {
  return Math.max(1, Math.min(MAX_HERO_LEVEL, Math.floor(level)));
}

export function heroXpRequiredForLevel(level: number): number {
  const normalizedLevel = normalizeLevel(level);

  if (normalizedLevel >= MAX_HERO_LEVEL) {
    return 0;
  }

  return 100 + (normalizedLevel - 1) * 25;
}

export function heroXpAtLevelStart(level: number): number {
  const completedLevels = normalizeLevel(level) - 1;
  return completedLevels * 100
    + (completedLevels * (completedLevels - 1) / 2) * 25;
}

export function heroLevelForExperience(experience: number): number {
  const normalizedExperience = Math.max(0, Math.floor(experience));
  let level = 1;

  while (
    level < MAX_HERO_LEVEL
    && normalizedExperience >= heroXpAtLevelStart(level + 1)
  ) {
    level += 1;
  }

  return level;
}

export interface HeroLevelProgress {
  level: number;
  xpIntoLevel: number;
  xpForNextLevel: number;
  progressPercent: number;
  xpUntilNextLevel: number;
  isMaxLevel: boolean;
}

export function getHeroLevelProgress(
  experience: number,
  storedLevel?: number
): HeroLevelProgress {
  const normalizedExperience = Math.max(0, Math.floor(experience));
  const level = storedLevel === undefined
    ? heroLevelForExperience(normalizedExperience)
    : normalizeLevel(storedLevel);
  const isMaxLevel = level >= MAX_HERO_LEVEL;
  const xpForNextLevel = heroXpRequiredForLevel(level);
  const xpIntoLevel = isMaxLevel
    ? 0
    : Math.max(
      0,
      Math.min(
        xpForNextLevel,
        normalizedExperience - heroXpAtLevelStart(level)
      )
    );

  return {
    level,
    xpIntoLevel,
    xpForNextLevel,
    progressPercent: isMaxLevel
      ? 100
      : Math.min(100, (xpIntoLevel / xpForNextLevel) * 100),
    xpUntilNextLevel: isMaxLevel ? 0 : xpForNextLevel - xpIntoLevel,
    isMaxLevel
  };
}
